import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { motion, AnimatePresence } from 'framer-motion';
import { userAtom, rememberMeAtom, isLoadingAtom, loginErrorAtom, tokenAtom } from '../atoms/authAtoms';
import { loginUser } from '../login/controller/login-controller';
import { ROUTES } from '../constants';
const Login = () => {
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // Global state
  const [, setUser] = useAtom(userAtom);
  const [rememberMe, setRememberMe] = useAtom(rememberMeAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [loginError, setLoginError] = useAtom(loginErrorAtom);
  const [token, setToken] = useAtom(tokenAtom);
  
  // UI state
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [formCompletion, setFormCompletion] = useState(0);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Refs
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const formRef = useRef(null);
  const carouselTimerRef = useRef(null);
  
  const navigate = useNavigate();
  
  // Featured product carousel data
  const featuredItems = [
    {
      id: 1,
      title: "Accessoires Exclusifs",
      description: "Découvrez notre nouvelle collection d'accessoires premium pour compléter votre style.",
      image: "https://images.unsplash.com/photo-1518002171953-a080ee817e1f?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      accent: "#FFFFFF"
    },
    {
      id: 2,
      title: "Style Premium",
      description: "Explorez notre sélection de vêtements haut de gamme conçus pour les connaisseurs.",
      image: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?q=80&w=1800&auto=format&fit=crop",
      accent: "#10B981"
    },
    {
      id: 3,
      title: "Élégance Moderne",
      description: "Une expérience shopping exceptionnelle avec les dernières tendances mode.",
      image: "https://images.unsplash.com/photo-1508427953056-b00b8d78ebf5?q=80&w=1800&auto=format&fit=crop",
      accent: "#8B5CF6"
    }
  ];
  
  // Rotate carousel every 6 seconds
  useEffect(() => {
    carouselTimerRef.current = setInterval(() => {
      setCarouselIndex(prev => (prev + 1) % featuredItems.length);
    }, 6000);
    
    return () => {
      clearInterval(carouselTimerRef.current);
    };
  }, []);
  
  // Clear errors on mount and focus username input
  useEffect(() => {
    setLoginError(null);
    
    // Focus username input on mount with a slight delay for animation
    setTimeout(() => {
      if (usernameRef.current) usernameRef.current.focus();
    }, 500);
    
    // Preload images for carousel
    featuredItems.forEach(item => {
      const img = new Image();
      img.src = item.image;
    });
    
  }, [setLoginError]);
  
  // Update form completion percentage
  useEffect(() => {
    let completion = 0;
    if (username) completion += 50;
    if (password) completion += 50;
    setFormCompletion(completion);
  }, [username, password]);
  
  // Form validation
  const validate = () => {
    let isValid = true;
    setLoginAttempted(true);
    
    // Validate username
    if (!username) {
      setUsernameError('Nom d\'utilisateur est requis');
      isValid = false;
    } else {
      setUsernameError('');
    }
    
    // Validate password
    if (!password) {
      setPasswordError('Mot de passe est requis');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    // Shake the form if validation fails
    if (!isValid && formRef.current) {
      formRef.current.classList.add('animate-shake');
      setTimeout(() => {
        if (formRef.current) formRef.current.classList.remove('animate-shake');
      }, 500);
    }
    
    return isValid;
  };
  
  // Form submission
  // In your handleSubmit function in Login.jsx
const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validate()) return;
  
  setIsLoading(true);
  setLoginError(null);
  
  try {
    // Call the loginUser function with username and password
    const authData = await loginUser({ username, password });
    setToken(authData.accessToken);
    
    // Set the user in your global state
    setUser(authData.user);
    
    // Store remember me preference if checked
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }
    
    // Success animation
    if (formRef.current) {
      formRef.current.classList.add('animate-success-pulse');
    }
    
    // Redirect based on user role
    setTimeout(() => {
      if (authData.user.isAdmin) {
        navigate(ROUTES.admin);
      } else {
        navigate(ROUTES.home);
      }
    }, 1000);
  } catch (error) {
    setLoginError(error.message || 'Nom d\'utilisateur ou mot de passe invalide');
    
    // Error animation
    if (formRef.current) {
      formRef.current.classList.add('animate-error-shake');
      setTimeout(() => {
        if (formRef.current) formRef.current.classList.remove('animate-error-shake');
      }, 500);
    }
  } finally {
    setIsLoading(false);
  }
};
  
  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
    // Refocus the input after toggling
    setTimeout(() => {
      if (passwordRef.current) passwordRef.current.focus();
    }, 10);
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 }
    }
  };
  
  const errorVariants = {
    hidden: { opacity: 0, y: -10, height: 0 },
    visible: { 
      opacity: 1, 
      y: 0, 
      height: 'auto',
      transition: { 
        type: "spring", 
        stiffness: 500, 
        damping: 30 
      }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      height: 0,
      transition: { 
        duration: 0.2 
      }
    }
  };
  
  // Image slider animation variants
  const sliderVariants = {
    initial: {
      opacity: 0,
      scale: 1.05
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };
  
  // Text animation variants
  const textVariants = {
    hidden: { 
      opacity: 0, 
      y: 20 
    },
    visible: {
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };
  
  const currentImage = featuredItems[carouselIndex];

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden">
      {/* Left side - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-white relative p-4 md:p-0">
        <div 
          className="w-full max-w-md px-6 py-10 md:px-10 md:py-12 relative"
          ref={formRef}
        >
          <div className="mb-10">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Connectez-vous</h1>
            <p className="text-gray-600">
              Heureux de vous revoir ! Accédez à votre compte pour découvrir nos nouveautés.
            </p>
            
            {/* Progress indicator */}
            <div className="h-1 w-36 bg-gray-100 rounded-full mt-6 overflow-hidden">
              <motion.div 
                className="h-full bg-[#174540]"
                initial={{ width: 0 }}
                animate={{ width: `${formCompletion}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
          
          <AnimatePresence>
            {loginError && (
              <motion.div 
                className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-start"
                variants={errorVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <svg className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">{loginError}</span>
              </motion.div>
            )}
          </AnimatePresence>
          
          <motion.form 
            className="space-y-6" 
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <div className="relative">
                <label htmlFor="username" className={`block text-sm font-medium transition-all duration-200 ${isUsernameFocused || username ? 'text-[#174540]' : 'text-gray-700'}`}>
                  Nom d'utilisateur
                </label>
                <div className={`mt-1 relative rounded-lg shadow-sm transition-all duration-300 ${isUsernameFocused ? 'ring-2 ring-[#174540]/50' : ''} ${usernameError && loginAttempted ? 'border-red-300' : 'border-gray-300'}`}>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className={`h-5 w-5 transition-colors duration-200 ${isUsernameFocused ? 'text-[#174540]' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    ref={usernameRef}
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onFocus={() => setIsUsernameFocused(true)}
                    onBlur={() => setIsUsernameFocused(false)}
                    className={`block w-full pl-10 pr-3 py-3 border focus:outline-none text-gray-900 rounded-lg ${usernameError && loginAttempted ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="Nom Utilisateur"
                  />
                  {username && (
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      onClick={() => setUsername('')}
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
                <AnimatePresence>
                  {usernameError && loginAttempted && (
                    <motion.p 
                      className="mt-2 text-sm text-red-600" 
                      variants={errorVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {usernameError}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <div className="relative">
                <label htmlFor="password" className={`block text-sm font-medium transition-all duration-200 ${isPasswordFocused || password ? 'text-[#174540]' : 'text-gray-700'}`}>
                  Mot de passe
                </label>
                <div className={`mt-1 relative rounded-lg shadow-sm transition-all duration-300 ${isPasswordFocused ? 'ring-2 ring-[#174540]/50' : ''} ${passwordError && loginAttempted ? 'border-red-300' : 'border-gray-300'}`}>
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className={`h-5 w-5 transition-colors duration-200 ${isPasswordFocused ? 'text-[#174540]' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    ref={passwordRef}
                    id="password"
                    name="password"
                    type={isPasswordVisible ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    className={`block w-full pl-10 pr-10 py-3 border focus:outline-none text-gray-900 rounded-lg ${passwordError && loginAttempted ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={togglePasswordVisibility}
                  >
                    {isPasswordVisible ? (
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
                <AnimatePresence>
                  {passwordError && loginAttempted && (
                    <motion.p 
                      className="mt-2 text-sm text-red-600" 
                      variants={errorVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {passwordError}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="relative inline-block w-10 align-middle select-none">
                  <input 
                    type="checkbox" 
                    name="remember-me" 
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only"
                  />
                  <div className="toggle-bg block h-6 w-11 rounded-full bg-gray-200 transition-colors duration-300 ease-in-out"></div>
                  <div 
                    className={`toggle-dot absolute left-1 top-1 h-4 w-4 rounded-full transition-transform duration-300 ease-in-out ${rememberMe ? 'transform translate-x-5 bg-[#174540]' : 'bg-white'}`}>
                  </div>
                </div>
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 select-none cursor-pointer">
                  Se souvenir de moi
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-[#174540] hover:text-[#0d2926] transition-colors duration-200">
                  Mot de passe oublié?
                </a>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <button
                type="submit"
                disabled={isLoading}
                className={`group w-full flex justify-center py-3.5 px-4 border border-transparent rounded-lg text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-[#174540] focus:ring-offset-2 shadow-lg transition-all duration-300 relative overflow-hidden ${isLoading ? 'bg-[#174540]/80' : 'bg-[#174540] hover:bg-[#0d2926]'}`}
              >
                <span className="absolute inset-0 w-full h-full transition-all duration-300 scale-x-0 origin-left group-hover:scale-x-100 bg-[#0d2926]"></span>
                
                <span className="relative flex items-center transition-transform group-hover:translate-x-1">
                  {isLoading ? (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span className="mr-2">Se connecter</span>
                  )}
                  <svg className="h-5 w-5 transition-transform group-hover:translate-x-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              </button>
            </motion.div>
            
            {/* <motion.div variants={itemVariants}>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Ou continuer avec</span>
                </div>
              </div>
            
              <div className="grid grid-cols-2 gap-3 mt-4">
                <motion.button
                  type="button"
                  className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-200 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-md group"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  <svg className="w-5 h-5 mr-2 text-[#4285F4] group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44-3.81 0-6.96-3.06-6.96-6.88s3.15-6.88 6.96-6.88c1.65 0 3.33.69 4.54 1.76l2.02-1.96C16.97 3.54 14.62 2.5 12.18 2.5c-5.42 0-9.82 4.4-9.82 9.82s4.4 9.82 9.82 9.82c5.16 0 9.31-3.73 9.31-9.19 0-.69-.07-1.35-.22-1.85z"/>
                  </svg>
                  Google
                </motion.button>
                
                <motion.button
                  type="button"
                  className="w-full inline-flex justify-center items-center py-2.5 px-4 border border-gray-200 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 hover:shadow-md group"
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  <svg className="w-5 h-5 mr-2 text-[#1877F2] group-hover:scale-110 transition-transform duration-200" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
                  </svg>
                  Facebook
                </motion.button>
              </div>
            </motion.div> */}
            
            {/* <motion.div variants={itemVariants} className="mt-4">
              <p className="text-center text-sm text-gray-600">
                Pas encore de compte?{' '}
                <Link to={ROUTES.signup} className="font-medium text-[#174540] hover:text-[#0d2926] transition-colors">
                  Créer un compte
                </Link>
              </p>
            </motion.div> */}
          </motion.form>
        </div>
      </div>
      
      {/* Right side - Brand showcase */}
      <div className="hidden md:block md:w-1/2 bg-[#174540] relative overflow-hidden">
        {/* Product Showcase */}
        <div className="h-full relative">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentImage.id}
              className="absolute inset-0 z-0"
              variants={sliderVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div 
                className="w-full h-full bg-cover bg-center"
                style={{ 
                  backgroundImage: `url(${currentImage.image})`, 
                }}
              >
                <div className="absolute inset-0 bg-black bg-opacity-40"></div>
              </div>
            </motion.div>
          </AnimatePresence>
          
          {/* Content overlay */}
          <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
            <div className="mb-20">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`content-${currentImage.id}`}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={textVariants}
                >
                  <div 
                    className="w-24 h-1 mb-6 rounded-full" 
                    style={{ backgroundColor: currentImage.accent }}
                  ></div>
                  <h2 className="text-4xl font-bold text-white mb-2">{currentImage.title}</h2>
                  <p className="text-white text-opacity-90 max-w-md mb-8">{currentImage.description}</p>
                  
                  <button 
                    className="inline-flex items-center text-white border-2 border-white rounded-full px-6 py-2.5 font-medium hover:bg-white hover:text-[#174540] transition-colors duration-300"
                  >
                    <span>Découvrir</span>
                    <svg className="ml-2 w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel indicators */}
            <div className="flex space-x-3">
              {featuredItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setCarouselIndex(index)}
                  className={`w-3 h-3 rounded-full ${carouselIndex === index ? 'bg-white' : 'bg-white/40'} focus:outline-none transition-all duration-300 hover:bg-white/80`}
                  aria-label={`View ${item.title}`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Brand logo */}
        <div className="absolute top-10 left-10 z-20">
          <div className="flex items-center">
            <img src="/logo.jpg" className="w-10 h-10 rounded-full" alt="Brand Logo" />
            <h2 className="ml-3 text-xl font-bold text-white">TENDRESSE</h2>
          </div>
        </div>
      </div>
      
      {/* Mobile showcase (visible only on mobile) */}
      <div className="block md:hidden bg-[#174540] h-48 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div 
            key={currentImage.id}
            className="absolute inset-0 z-0"
            variants={sliderVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <div 
              className="w-full h-full bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${currentImage.image})`, 
              }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        <div className="absolute inset-0 flex items-center justify-center p-4 z-10">
          <div className="text-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={`mobile-content-${currentImage.id}`}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={textVariants}
              >
                <h2 className="text-2xl font-bold text-white mb-1">{currentImage.title}</h2>
                <p className="text-white text-opacity-90 text-sm">{currentImage.description}</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        
        {/* Mobile carousel indicators */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 z-20">
          {featuredItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setCarouselIndex(index)}
              className={`w-2 h-2 rounded-full ${carouselIndex === index ? 'bg-white' : 'bg-white/40'} focus:outline-none transition-all duration-300`}
              aria-label={`View ${item.title}`}
            />
          ))}
        </div>
      </div>
      
      {/* Add global animation styles */}
      <style jsx global>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          50% { transform: translateX(8px); }
          75% { transform: translateX(-4px); }
        }
        
        .animate-shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        }
        
        .animate-error-shake {
          animation: shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
          background-color: rgba(254, 226, 226, 0.4);
        }
        
        /* Custom toggle switch styles */
        .toggle-bg:after {
          content: '';
          @apply absolute top-0.5 left-0.5 bg-white border border-gray-300 rounded-full h-5 w-5 transition-all duration-300 ease-in-out;
        }
        
        input:checked + .toggle-bg:after {
          transform: translateX(100%);
          @apply border-white;
        }
        
        input:checked + .toggle-bg {
          @apply bg-[#174540] border-[#174540];
        }
      `}</style>
    </div>
  );
};

export default Login;
